import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnswerRefScope } from './answer-ref-context';

import type { BlockIssue } from './block-issues';

import { useTaskVariables } from '@/contexts/task-variables-context';
import { useCapabilities } from '@/hooks/use-capabilities';
import { variableRungIssues } from '@/lib/variable-rung-issue';

/**
 * A VARIABLE IN A BLOCK WHOSE RUNG DOES NOT SUPPORT ONE, reported on the block that holds it.
 *
 * The fifth cross-cutting check on this panel, and it is here for the reason all of them are: no
 * block can compute it. A block knows what it does with a variable; whether a variable may be in
 * it at all is a question about the teacher's capability level, and seventeen editors each asking
 * it would be seventeen chances to ask it differently.
 *
 * ## Why the board alone was not enough
 *
 * The check first landed in `issuesForBlock`, which reads correctly on the board and never runs
 * in an editor: a block's own card computes its issues by calling its `<type>Issues` directly
 * with LIVE data, and never goes through the dispatcher. So the error panel named the fault while
 * the card the teacher had just pasted into said nothing — the owner's report was simply "i'm
 * still able to paste variable chips into mcq at rung 1".
 *
 * Both paths now call `variableRungIssues`, which is why that function sits in a leaf module:
 * the dispatcher imports every block file, and every block file imports this panel, so the panel
 * importing the dispatcher would close a cycle.
 *
 * ## Where its inputs come from
 *
 * The block's TYPE and DATA from the answer-ref scope, which already holds every task; the
 * variables IN FORCE from the task-variables context, which is the task's own plus the
 * experience's. Both are already mounted around every block editor, so this needs no prop and no
 * block file changes — the same arrangement the other four use.
 */
export function useOutOfRungVariableIssues(blockId: string | undefined): BlockIssue[] {
  const { t } = useTranslation();
  const scope = useAnswerRefScope();
  const variables = useTaskVariables();
  const { level } = useCapabilities();
  const rung = level('variables');

  const tasks = scope?.tasks;
  return useMemo(() => {
    if (!blockId || !tasks) return [];
    /*
     * Searched across every task rather than the current one. A block editor is mounted in the
     * task it belongs to, but summary-feedback blocks are not, and a wrong "not found" here
     * fails silent — which is the one failure this check cannot afford.
     */
    const block = tasks
      .flatMap(task => task.blocks ?? [])
      .find(b => b.id === blockId);
    if (!block) return [];
    return variableRungIssues(block, rung, variables, t);
  }, [blockId, tasks, rung, variables, t]);
}
